import type { Dispatch, SetStateAction } from "react";
import type { RtoTermDefinition } from "@/lib/rto/rtoTermSettings";
import {
  cardDividerBorderClass,
  cardMutedTextClass,
  cardTitleTextClass,
  compactInputClass,
  insetSurfaceCardClass,
} from "@/components/common/surfaceClasses";
import { RtoSettingsTermRow } from "@/components/ops/RtoSettingsTermRow";
import { normalizeStateCode } from "@/components/ops/rtoSettingsSectionHelpers";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export function RtoSettingsOrgTermsEditor(props: {
  rtoTerms: RtoTermDefinition[];
  setRtoTerms: Dispatch<SetStateAction<RtoTermDefinition[]>>;
  rtoStates: string[];
  rtoStateInput: string;
  setRtoStateInput: Dispatch<SetStateAction<string>>;
  showRtoStateEditor: boolean;
  setShowRtoStateEditor: Dispatch<SetStateAction<boolean>>;
  saving: boolean;
  addRtoState: () => void;
  removeRtoState: (code: string) => void;
  openStateOverridesByTerm: Record<string, boolean>;
  setOpenStateOverridesByTerm: Dispatch<SetStateAction<Record<string, boolean>>>;
  openRtoActionsFor: string | null;
  setOpenRtoActionsFor: Dispatch<SetStateAction<string | null>>;
}) {
  const {
    rtoTerms,
    setRtoTerms,
    rtoStates,
    rtoStateInput,
    setRtoStateInput,
    showRtoStateEditor,
    setShowRtoStateEditor,
    saving,
    addRtoState,
    removeRtoState,
    openStateOverridesByTerm,
    setOpenStateOverridesByTerm,
    openRtoActionsFor,
    setOpenRtoActionsFor,
  } = props;

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "rto-terms-table" }}>
      <div className={`mb-3 ${insetSurfaceCardClass}`}>
        <p className={`text-xs font-semibold ${cardTitleTextClass}`}>RTO states this org does business in</p>
        <p className={`mt-1 text-[11px] ${cardMutedTextClass}`}>Set active states once; each term can then override payoff % by state.</p>
        <button
          type="button"
          className="mt-2 text-xs text-indigo-700 underline underline-offset-2 hover:text-indigo-800"
          onClick={() => setShowRtoStateEditor((prev) => !prev)}
        >
          {showRtoStateEditor ? "Hide state add box" : "Add/edit states"}
        </button>
        {showRtoStateEditor ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              className={`w-24 text-xs uppercase ${compactInputClass}`}
              placeholder="SC"
              value={rtoStateInput}
              onChange={(e) => setRtoStateInput(normalizeStateCode(e.target.value))}
              maxLength={2}
            />
            <button
              type="button"
              className="text-xs rounded border border-indigo-300 bg-indigo-50 text-indigo-700 px-2 py-1.5 hover:bg-indigo-100 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:bg-indigo-950/55"
              onClick={addRtoState}
            >
              Add state
            </button>
            {rtoStates.length === 0 ? (
              <span className={`text-[11px] ${cardMutedTextClass}`}>No state overrides yet (using each term default payoff %).</span>
            ) : null}
          </div>
        ) : null}
        {rtoStates.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {rtoStates.map((st) => (
              <span
                key={st}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${cardDividerBorderClass} dark:bg-zinc-800/60`}
              >
                {st}
                <button
                  type="button"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => removeRtoState(st)}
                  title={`Remove ${st}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div className="mb-2">
        <button
          type="button"
          disabled={saving}
          onClick={() =>
            setRtoTerms((prev) => [
              ...prev,
              {
                id: `rto-${Date.now()}`,
                name: "Name",
                term_months: 1,
                divisor: 1,
                required_down_type: "payments",
                required_down_value: 1,
                balloon_required: false,
                balloon_payments: 0,
                early_purchase_percent: 0.5,
                early_purchase_percent_by_state: Object.fromEntries(rtoStates.map((st) => [st, 0.5])),
              },
            ])
          }
          className="shrink-0 px-2.5 py-1.5 rounded-lg border border-dashed border-indigo-400 text-indigo-600 text-xs hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
        >
          + Add term
        </button>
      </div>
      <div className={`mb-1 grid grid-cols-1 sm:grid-cols-8 gap-2 px-1 text-[11px] font-semibold ${cardMutedTextClass}`}>
        <div>Name</div>
        <div>Months</div>
        <div>Divisor</div>
        <div>Down Type</div>
        <div>Down Value</div>
        <div>Balloon?</div>
        <div>Balloon Pmts</div>
        <div>Actions</div>
      </div>
      <div className="space-y-2">
        {rtoTerms.map((term, idx) => (
          <RtoSettingsTermRow
            key={term.id}
            term={term}
            idx={idx}
            rowCount={rtoTerms.length}
            rtoStates={rtoStates}
            openStateOverridesByTerm={openStateOverridesByTerm}
            setOpenStateOverridesByTerm={setOpenStateOverridesByTerm}
            openRtoActionsFor={openRtoActionsFor}
            setOpenRtoActionsFor={setOpenRtoActionsFor}
            setRtoTerms={setRtoTerms}
          />
        ))}
      </div>
    </div>
  );
}
